/**
 * StickyHeader 组件
 * 功能：处理页面的粘性头部功能，包括滚动控制、菜单交互等
 * 作者：Tuxedo主题开发团队
 * 创建时间：2024-12-19
 * 最后修改：2024-12-19
 */

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();

    // 处理菜单项交互
    this.setupMenuInteractions();
  }

  /**
   * 设置菜单交互事件
   */
  setupMenuInteractions() {
    const headerInlineMenu = Array.from(document.querySelectorAll('.header__menu-item'));
    
    headerInlineMenu.forEach(item => {
      const detailsElement = item.closest('details'); 
      const megaMenuContent = detailsElement ? detailsElement.querySelector('.mega-menu__content') : null; 
      
      item.addEventListener('mouseenter', () => {
        this.header.classList.add('scrolled-past-header');
      });
      
      item.addEventListener('mouseleave', (event) => {
        const isInMegaMenu = megaMenuContent && megaMenuContent.contains(event.relatedTarget);
        const isInHeaderMenu = headerInlineMenu.some(menuItem =>
          menuItem.contains(event.relatedTarget)
        );
        const hasExpandedMenu = headerInlineMenu.some(menuItem => 
          menuItem.getAttribute('aria-expanded') === 'true'
        );
        
        if (!isInMegaMenu && !isInHeaderMenu && !hasExpandedMenu) {
          this.header.classList.remove('scrolled-past-header');
        }
      });
    });

    // 全局监听点击事件
    document.addEventListener('click', (event) => {
      const isInHeaderMenu = Array.from(headerInlineMenu).some(menuItem =>
        menuItem.contains(event.target)
      );

      const isInAnyMegaMenu = Array.from(headerInlineMenu).some(menuItem => {
        const detailsElement = menuItem.closest('details');
        const megaMenuContent = detailsElement ? detailsElement.querySelector('.mega-menu__content') : null;
        return megaMenuContent && megaMenuContent.contains(event.target);
      });

      // 如果点击的位置既不在 header__menu-item，也不在任何 mega-menu__content 中
      if (!isInHeaderMenu && !isInAnyMegaMenu) {
        this.header.classList.remove('scrolled-past-header');
      }
    });
  }

  /**
   * 设置头部高度
   */
  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  /**
   * 组件断开连接时的清理工作
   */
  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  /**
   * 创建IntersectionObserver来监听头部边界
   */
  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  /**
   * 处理滚动事件
   */
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  /**
   * 隐藏头部
   */
  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
  }

  /**
   * 显示头部
   */
  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  /**
   * 重置头部状态
   */
  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  /**
   * 关闭菜单展开
   */
  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach(disclosure => disclosure.close());
  }

  /**
   * 关闭搜索模态框
   */
  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (this.searchModal) {
      this.searchModal.close(false);
    }
  }
}

// 注册自定义元素
customElements.define('sticky-header', StickyHeader);
